/**
 * CashRunwayChip
 *
 * Compact chip surfacing QuickBooks cash-runway inside the Health Score hero.
 * Data source: GET /api/quickbooks/cash-runway
 *
 * Palette: strong/profitable => Gold, healthy => Blue, watch => Orange,
 *          critical => Purple. No red/green.
 *
 * If the tenant has no live QBO snapshot, the chip renders a subtle "Connect
 * QuickBooks" CTA linking to the Integrations page.
 */
import { useEffect, useState } from "react";
import { Wallet, ArrowUpRight, Zap } from "lucide-react";
import api from "../../../utils/api";

const TONE = {
  gold:   { bg: "#FEF3E6", border: "#D4A017", ink: "#A16207", label: "#1C2040" },
  blue:   { bg: "#EFF6FF", border: "#1B75BA", ink: "#14588A", label: "#1C2040" },
  orange: { bg: "#FED7AA", border: "#F5841F", ink: "#EA580C", label: "#1C2040" },
  purple: { bg: "#F4F6FB", border: "#8B5CF6", ink: "#282560", label: "#1C2040" },
  neutral:{ bg: "#F4F6FB", border: "#E8EAF0", ink: "#4B5570", label: "#1C2040" },
};

function fmtMoney(v, currency = "USD") {
  if (v === null || v === undefined) return "--";
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency, maximumFractionDigits: 0 }).format(v);
  } catch {
    return `$${Math.round(v).toLocaleString()}`;
  }
}

function fmtMonths(v) {
  if (v === null || v === undefined) return null;
  if (v >= 60) return "60+ months";
  return `${v} month${v === 1 ? "" : "s"}`;
}

export function CashRunwayChip() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/quickbooks/cash-runway");
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "failed");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    const p = TONE.neutral;
    return (
      <div
        data-testid="cash-runway-chip-loading"
        className="rounded-lg px-3 py-2 animate-pulse"
        style={{ background: p.bg, border: `1px solid ${p.border}`, minHeight: 60 }}
      />
    );
  }

  if (error || !data || data.available === false) {
    const p = TONE.neutral;
    return (
      <a
        href="/integrations"
        data-testid="cash-runway-chip-cta"
        className="rounded-lg px-3 py-2 flex items-center gap-2 transition-shadow hover:shadow-sm"
        style={{ background: p.bg, border: `1px dashed ${p.border}` }}
      >
        <Zap size={16} style={{ color: "#1B75BA" }} />
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "#6B7491" }}>
            Cash Runway
          </p>
          <p className="text-[11px] font-semibold truncate" style={{ color: "#1B75BA" }}>
            Connect QuickBooks to unlock live runway
          </p>
        </div>
        <ArrowUpRight size={13} style={{ color: "#1B75BA" }} />
      </a>
    );
  }

  const tone = TONE[data.tier_color_key] || TONE.blue;
  const status = data.status;

  let headline;
  let sub;
  if (status === "profitable") {
    headline = "Profitable";
    sub = `${fmtMoney(data.net_income, data.currency)} net income - no burn`;
  } else if (status === "no_burn") {
    headline = "No Burn Detected";
    sub = `${fmtMoney(data.cash_on_hand, data.currency)} cash on hand`;
  } else if (data.months !== null && data.months !== undefined) {
    headline = fmtMonths(data.months);
    sub = `${fmtMoney(data.cash_on_hand, data.currency)} cash / ${fmtMoney(data.avg_monthly_burn, data.currency)} avg burn`;
  } else {
    headline = "Runway";
    sub = data.message || "";
  }

  return (
    <div
      data-testid="cash-runway-chip"
      className="rounded-lg px-3 py-2 flex items-center gap-2.5"
      style={{ background: tone.bg, border: `1px solid ${tone.border}` }}
      role="status"
      aria-label={`Cash runway ${headline}. ${sub}`}
    >
      <div
        className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
        style={{ background: "#FFFFFF", border: `1px solid ${tone.border}` }}
      >
        <Wallet size={14} style={{ color: tone.ink }} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "#6B7491" }}>
            Cash Runway
          </span>
          <span
            className="text-[8px] font-bold px-1.5 py-0.5 rounded"
            style={{ background: tone.border, color: "#FFFFFF" }}
            data-testid="cash-runway-live-badge"
          >
            LIVE
          </span>
        </div>
        <p
          className="text-sm font-black leading-tight truncate"
          style={{ color: tone.label }}
          data-testid="cash-runway-headline"
        >
          {headline}
        </p>
        <p className="text-[10px] truncate" style={{ color: "#6B7491" }} data-testid="cash-runway-sub">
          {sub}
        </p>
      </div>
    </div>
  );
}

export default CashRunwayChip;
